import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  IconButton,
  Tooltip,
  FormControlLabel,
  Switch,
  Card,
  CardContent,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DocumentScannerIcon from '@mui/icons-material/DocumentScanner';
import { useNavigate, useParams } from 'react-router-dom';
import { subClientService, subClientGroupService, paysService, reservationService } from '../services/api';
import type { SubClient, SubClientGroup, Reservation, Pays } from '../types';
import IDScannerModal from '../components/FieldAgent/IDScannerModal';

const RESERVATION_STATUS: Record<string, { label: string; color: 'default' | 'warning' | 'info' | 'success' | 'error' }> = {
  pending: { label: 'En attente', color: 'warning' },
  confirmed: { label: 'Confirmée', color: 'info' },
  completed: { label: 'Terminée', color: 'success' },
  cancelled: { label: 'Annulée', color: 'error' },
};

const ALLOWED_DOC_MIME = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
];

function formatDateTime(iso?: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return '—';
  }
}

function openDocumentUrl(doc: { file_url?: string; file_path?: string }): void {
  const u = doc.file_url || doc.file_path;
  if (!u) return;
  const href = u.startsWith('http') ? u : `${window.location.origin}/storage/${String(u).replace(/^\/+/, '')}`;
  window.open(href, '_blank', 'noopener,noreferrer');
}

type SheetForm = {
  nom: string;
  prenom: string;
  email: string;
  phone: string;
  type: 'individual' | 'company';
  company_name: string;
  siret: string;
  address: string;
  city: string;
  postal_code: string;
  country: string;
  notes: string;
  is_active: boolean;
  sub_client_group_id: number | '';
};

const SubClientSheet: React.FC = () => {
  const { subClientId } = useParams<{ subClientId: string }>();
  const navigate = useNavigate();
  const id = subClientId && /^\d+$/.test(subClientId) ? Number(subClientId) : NaN;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paysList, setPaysList] = useState<Pays[]>([]);
  const [groups, setGroups] = useState<SubClientGroup[]>([]);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [documentTypes, setDocumentTypes] = useState<Record<string, string>>({});

  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [documentType, setDocumentType] = useState('');
  const [documentDescription, setDocumentDescription] = useState('');
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [idScannerOpen, setIdScannerOpen] = useState(false);

  const [form, setForm] = useState<SheetForm>({
    nom: '',
    prenom: '',
    email: '',
    phone: '',
    type: 'individual',
    company_name: '',
    siret: '',
    address: '',
    city: '',
    postal_code: '',
    country: '',
    notes: '',
    is_active: true,
    sub_client_group_id: '',
  });

  const loadReservations = useCallback(async () => {
    if (!Number.isFinite(id)) return;
    try {
      const res = await reservationService.getList({
        sub_client_id: id,
        per_page: 100,
        page: 1,
        include_cancelled: true,
      });
      const payload = res.data as { data?: Reservation[] };
      setReservations(Array.isArray(payload.data) ? payload.data : []);
    } catch {
      setReservations([]);
    }
  }, [id]);

  const loadDocuments = useCallback(async () => {
    if (!Number.isFinite(id)) return;
    try {
      const res = await subClientService.getDocuments(id);
      setDocuments(res.data.data || []);
    } catch {
      setDocuments([]);
    }
  }, [id]);

  useEffect(() => {
    paysService.getAll().then((r) => setPaysList(r.data.data || [])).catch(() => {});
    subClientService.getDocumentTypes().then((r) => setDocumentTypes(r.data.data || {})).catch(() => {});
    subClientGroupService.getAll().then((r) => setGroups(r.data.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!Number.isFinite(id)) {
      setLoading(false);
      setError('Client invalide.');
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await subClientService.getById(id);
        const c = res.data.data as SubClient;
        if (cancelled) return;
        setForm({
          nom: c.nom || '',
          prenom: c.prenom || '',
          email: c.email || '',
          phone: c.phone || '',
          type: c.type || 'individual',
          company_name: c.company_name || '',
          siret: c.siret || '',
          address: c.address || '',
          city: c.city || '',
          postal_code: c.postal_code || '',
          country: c.country || '',
          notes: c.notes || '',
          is_active: !!c.is_active,
          sub_client_group_id: c.sub_client_group_id ?? '',
        });
        await Promise.all([loadReservations(), loadDocuments()]);
      } catch (e: any) {
        if (!cancelled) setError(e.response?.data?.message || 'Impossible de charger le client.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, loadReservations, loadDocuments]);

  const handleSave = async () => {
    if (!Number.isFinite(id)) return;
    setSaving(true);
    setError(null);
    try {
      const fullName = `${form.nom || ''} ${form.prenom || ''}`.trim();
      const { sub_client_group_id, ...rest } = form;
      await subClientService.update(id, {
        ...rest,
        name: fullName || form.nom,
        sub_client_group_id: sub_client_group_id === '' ? null : sub_client_group_id,
      });
      await loadDocuments();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Enregistrement impossible.');
    } finally {
      setSaving(false);
    }
  };

  const handleDocumentFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!ALLOWED_DOC_MIME.includes(file.type)) {
      setError('Fichier non pris en charge (PDF, images ou Word).');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('Le fichier doit faire moins de 10 Mo.');
      return;
    }
    setDocumentFile(file);
    setError(null);
  };

  const handleDocumentUpload = async () => {
    if (!Number.isFinite(id) || !documentFile || !documentType) return;
    setUploadingDocument(true);
    setError(null);
    try {
      await subClientService.uploadDocument(id, documentFile, documentType, documentDescription || undefined);
      setDocumentFile(null);
      setDocumentType('');
      setDocumentDescription('');
      await loadDocuments();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Échec du téléversement.');
    } finally {
      setUploadingDocument(false);
    }
  };

  const handleDocumentDelete = async (documentId: number) => {
    if (!Number.isFinite(id)) return;
    if (!window.confirm('Supprimer ce document ?')) return;
    setError(null);
    try {
      await subClientService.deleteDocument(id, documentId);
      await loadDocuments();
    } catch (e: any) {
      setError(e.response?.data?.message || 'Suppression impossible.');
    }
  };

  const clientDisplayName =
    form.type === 'company' && form.company_name
      ? form.company_name
      : `${form.prenom || ''} ${form.nom || ''}`.trim() || `Client N° ${id}`;

  if (!Number.isFinite(id)) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Identifiant client invalide.</Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/sub-clients')} sx={{ mt: 2 }}>Retour</Button>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f6fa', pb: 4 }}>
      <Box sx={{ bgcolor: 'white', borderBottom: '1px solid #e0e4ea', px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/sub-clients')} sx={{ textTransform: 'none' }}>Retour à la liste</Button>
        <Typography variant="h6" fontWeight={700} sx={{ flex: 1, color: '#2c3e50' }}>
          Fiche client N° {id}
        </Typography>
        <Button variant="contained" startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />} onClick={handleSave} disabled={saving} sx={{ textTransform: 'none', bgcolor: '#2c3e50' }}>
          Enregistrer
        </Button>
      </Box>

      <Box sx={{ maxWidth: 1400, mx: 'auto', px: 2, pt: 3 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
        ) : (
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 7 }}>
              <Paper sx={{ p: 3, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>Modifier la fiche</Typography>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Type</InputLabel>
                      <Select value={form.type} label="Type" onChange={(e) => setForm((f) => ({ ...f, type: e.target.value as 'individual' | 'company' }))}>
                        <MenuItem value="individual">Particulier</MenuItem>
                        <MenuItem value="company">Entreprise</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Groupe</InputLabel>
                      <Select
                        label="Groupe"
                        value={form.sub_client_group_id === '' ? '' : String(form.sub_client_group_id)}
                        onChange={(e) => {
                          const v = e.target.value;
                          setForm((f) => ({ ...f, sub_client_group_id: v === '' ? '' : Number(v) }));
                        }}
                      >
                        <MenuItem value="">— Aucun groupe —</MenuItem>
                        {groups.map((g) => (
                          <MenuItem key={g.id} value={String(g.id)}>{g.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" label="Nom" value={form.nom} onChange={(e) => setForm((f) => ({ ...f, nom: e.target.value }))} /></Grid>
                  <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" label="Prénom" value={form.prenom} onChange={(e) => setForm((f) => ({ ...f, prenom: e.target.value }))} /></Grid>
                  {form.type === 'company' && (
                    <>
                      <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" label="Société" value={form.company_name} onChange={(e) => setForm((f) => ({ ...f, company_name: e.target.value }))} /></Grid>
                      <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" label="SIRET" value={form.siret} onChange={(e) => setForm((f) => ({ ...f, siret: e.target.value }))} /></Grid>
                    </>
                  )}
                  <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" label="Email" type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></Grid>
                  <Grid size={{ xs: 12, sm: 6 }}><TextField fullWidth size="small" label="Téléphone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} /></Grid>
                  <Grid size={12}><TextField fullWidth size="small" label="Adresse" value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} /></Grid>
                  <Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth size="small" label="Code postal" value={form.postal_code} onChange={(e) => setForm((f) => ({ ...f, postal_code: e.target.value }))} /></Grid>
                  <Grid size={{ xs: 12, sm: 4 }}><TextField fullWidth size="small" label="Ville" value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} /></Grid>
                  <Grid size={{ xs: 12, sm: 4 }}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Pays</InputLabel>
                      <Select label="Pays" value={form.country} onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))} MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}>
                        <MenuItem value="">—</MenuItem>
                        {paysList.map((p) => <MenuItem key={p.id} value={p.nom_fr_fr}>{p.nom_fr_fr}</MenuItem>)}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid size={12}><TextField fullWidth size="small" label="Notes" multiline minRows={2} value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} /></Grid>
                  <Grid size={12}>
                    <FormControlLabel control={<Switch checked={form.is_active} onChange={(e) => setForm((f) => ({ ...f, is_active: e.target.checked }))} />} label="Client actif" />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, lg: 5 }}>
              <Paper sx={{ p: 2, borderRadius: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight={700}>Réservations</Typography>
                  <Tooltip title="Liste filtrée (nouvel onglet)">
                    <IconButton size="small" onClick={() => window.open(`${window.location.origin}/reservations?sub_client_id=${id}`, '_blank', 'noopener,noreferrer')}><EventAvailableIcon fontSize="small" /></IconButton>
                  </Tooltip>
                </Box>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Réf.</TableCell>
                        <TableCell>Départ</TableCell>
                        <TableCell>Statut</TableCell>
                        <TableCell align="right" />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {reservations.length === 0 ? (
                        <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary', py: 2 }}>Aucune réservation</TableCell></TableRow>
                      ) : reservations.map((r) => {
                        const st = RESERVATION_STATUS[r.status] || { label: r.status, color: 'default' as const };
                        return (
                          <TableRow key={r.id} hover>
                            <TableCell sx={{ fontWeight: 600 }}>{r.reference || r.id}</TableCell>
                            <TableCell>{formatDateTime(r.pickup_datetime)}</TableCell>
                            <TableCell><Chip label={st.label} size="small" color={st.color} sx={{ fontSize: '0.7rem' }} /></TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => window.open(`${window.location.origin}/reservations/${r.id}`, '_blank', 'noopener,noreferrer')}><OpenInNewIcon fontSize="small" /></IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>

              <Paper id="zone-documents" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>Pièces jointes</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  RIB, KBIS, pièce d&apos;identité, permis : ajoutez les fichiers ici ou via le scanner.
                </Typography>
                <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<PhotoCameraIcon />}
                    onClick={() => window.open(`${window.location.origin}/condition-assessments?subClientId=${id}`, '_blank', 'noopener,noreferrer')}
                    sx={{ textTransform: 'none' }}
                  >
                    États des lieux
                  </Button>
                  <Button size="small" variant="outlined" startIcon={<DocumentScannerIcon />} onClick={() => setIdScannerOpen(true)} sx={{ textTransform: 'none' }}>
                    Scanner un document
                  </Button>
                </Stack>

                <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
                  <CardContent sx={{ py: 2, '&:last-child': { pb: 2 } }}>
                    <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1.5 }}>Ajouter un document</Typography>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <FormControl fullWidth size="small">
                          <InputLabel>Type</InputLabel>
                          <Select value={documentType} label="Type" onChange={(e) => setDocumentType(e.target.value)}>
                            {Object.entries(documentTypes).map(([key, label]) => (
                              <MenuItem key={key} value={key}>{label}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <input
                          accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx"
                          style={{ display: 'none' }}
                          id="subclient-sheet-doc-input"
                          type="file"
                          onChange={handleDocumentFileChange}
                        />
                        <label htmlFor="subclient-sheet-doc-input">
                          <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />} fullWidth size="small" sx={{ textTransform: 'none' }}>
                            {documentFile ? documentFile.name : 'Choisir un fichier'}
                          </Button>
                        </label>
                      </Grid>
                      <Grid size={12}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Description (optionnel)"
                          multiline
                          minRows={2}
                          value={documentDescription}
                          onChange={(e) => setDocumentDescription(e.target.value)}
                        />
                      </Grid>
                      <Grid size={12}>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleDocumentUpload}
                          disabled={!documentFile || !documentType || uploadingDocument}
                          startIcon={uploadingDocument ? <CircularProgress size={18} color="inherit" /> : <CloudUploadIcon />}
                          sx={{ textTransform: 'none', bgcolor: '#2c3e50' }}
                        >
                          {uploadingDocument ? 'Envoi…' : 'Téléverser'}
                        </Button>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {documents.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">Aucun document enregistré.</Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Type</TableCell>
                          <TableCell>Fichier</TableCell>
                          <TableCell align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {documents.map((doc: any) => (
                          <TableRow key={doc.id} hover>
                            <TableCell>
                              <Chip
                                label={documentTypes[doc.document_type] || doc.document_type_label || doc.document_type || 'Document'}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell sx={{ maxWidth: 180 }}>{doc.original_name || '—'}</TableCell>
                            <TableCell align="right">
                              <Tooltip title="Ouvrir">
                                <IconButton size="small" onClick={() => openDocumentUrl(doc)} color="primary"><VisibilityIcon fontSize="small" /></IconButton>
                              </Tooltip>
                              <Tooltip title="Supprimer">
                                <IconButton size="small" onClick={() => handleDocumentDelete(doc.id)} color="error"><DeleteOutlineIcon fontSize="small" /></IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}
      </Box>

      <IDScannerModal
        open={idScannerOpen}
        onClose={() => setIdScannerOpen(false)}
        existingClientId={id}
        existingClientName={clientDisplayName}
        onDocumentAttached={() => {
          setIdScannerOpen(false);
          loadDocuments();
        }}
      />
    </Box>
  );
};

export default SubClientSheet;
